import React, { useState, useEffect, useRef } from 'react';
import { CloudArrowUpIcon, XMarkIcon } from '@heroicons/react/24/outline';
import pageContentService, { PageContent as PageContentType, PageSection } from '../services/pageContent';
import Toast from '../components/Toast';
import AuthenticatedImage from '../components/AuthenticatedImage';
import ImageCropModal from '../components/ImageCropModal';

const PageContent: React.FC = () => {
  const [pages, setPages] = useState<PageContentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState<PageContentType | null>(null);
  const [toast, setToast] = useState<{ id: string; type: 'success' | 'error'; title: string; message?: string } | null>(null);
  
  // File upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);
  const [showExistingBanner, setShowExistingBanner] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [rawCropSrc, setRawCropSrc] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    slug: '',
    bannerImageUrl: '',
    sections: [] as PageSection[]
  });

  useEffect(() => {
    loadPages();
  }, []);

  useEffect(() => {
    if (pages.length > 0 && !currentPage) {
      // Initialize form with the first page (Information page)
      const page = pages[0];
      setCurrentPage(page);
      setSelectedFile(null);
      setPreviewUrl('');
      setShowExistingBanner(!!page.bannerImageUrl);
      setFormData({
        slug: page.slug,
        bannerImageUrl: page.bannerImageUrl || '',
        sections: initializeFormSections(page.sections)
      });
    }
  }, [pages, currentPage]);

  const loadPages = async () => {
    try {
      setLoading(true);
      const response = await pageContentService.getAllPages();
      setPages(response.data);
    } catch (error) {
      console.error('Error loading pages:', error);
      setToast({ 
        id: Date.now().toString(), 
        type: 'error', 
        title: 'Error', 
        message: 'Failed to load pages' 
      });
    } finally {
      setLoading(false);
    }
  };

  const initializeFormSections = (sections: PageSection[] = []): PageSection[] => {
    // Ensure we have the required sections with proper structure
    const contactUsSection = sections.find(s => s.header.toLowerCase().includes('contact')) || {
      header: 'Contact Us',
      links: [{ text: '', url: '' }, { text: '', url: '' }, { text: '', url: '' }]
    };
    
    const packagesSection = sections.find(s => s.header.toLowerCase().includes('package')) || {
      header: 'Packages',
      links: [{ text: '', url: '' }, { text: '', url: '' }]
    };
    
    // Ensure Contact Us has exactly 3 links
    while (contactUsSection.links.length < 3) {
      contactUsSection.links.push({ text: '', url: '' });
    }
    contactUsSection.links = contactUsSection.links.slice(0, 3);
    
    // Ensure Packages has exactly 2 links
    while (packagesSection.links.length < 2) {
      packagesSection.links.push({ text: '', url: '' });
    }
    packagesSection.links = packagesSection.links.slice(0, 2);
    
    return [contactUsSection, packagesSection];
  };


  const handleSave = async () => {

    // Validate sections - check if all links have both text and URL
    for (let i = 0; i < formData.sections.length; i++) {
      const section = formData.sections[i];
      for (let j = 0; j < section.links.length; j++) {
        const link = section.links[j];
        if (!link.text.trim() || !link.url.trim()) {
          setToast({
            id: Date.now().toString(),
            type: 'error',
            title: 'Validation Error',
            message: `All links in ${section.header} must have both text and URL`
          });
          return;
        }
      }
    }

    try {
      // First upload banner if a new file was selected
      let bannerImageUrl = formData.bannerImageUrl;
      if (selectedFile) {
        setIsUploading(true);
        const uploadResult = await pageContentService.uploadBanner(
          selectedFile,
          (progress) => setUploadProgress(progress)
        );
        bannerImageUrl = uploadResult.url;
        setIsUploading(false);
      }
      
      if (currentPage) {
        await pageContentService.updatePage(currentPage.id, {
          ...formData,
          bannerImageUrl
        });
        setToast({ 
          id: Date.now().toString(), 
          type: 'success', 
          title: 'Success', 
          message: 'Page updated successfully' 
        });
        loadPages();
      }
    } catch (error: any) {
      console.error('Error saving page:', error);
      setIsUploading(false);
      setToast({ 
        id: Date.now().toString(),
        type: 'error', 
        title: 'Error',
        message: error.response?.data?.error || 'Failed to save page' 
      });
    }
  };

  // File upload handlers
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      openPageCrop(e.dataTransfer.files[0]);
    }
  };

  const openPageCrop = (file: File) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setRawCropSrc(reader.result as string);
        setShowCropModal(true);
      };
      reader.readAsDataURL(file);
    } else {
      setToast({
        id: Date.now().toString(),
        type: 'error',
        title: 'Invalid File',
        message: 'Please select a valid image file'
      });
    }
  };

  const handlePageCropComplete = (croppedBlob: Blob) => {
    const croppedFile = new File([croppedBlob], 'page-banner.jpg', { type: 'image/jpeg' });
    setSelectedFile(croppedFile);
    const url = URL.createObjectURL(croppedBlob);
    setPreviewUrl(url);
    setShowExistingBanner(false);
    setShowCropModal(false);
    setRawCropSrc(null);
  };

  const handlePageCropCancel = () => {
    setShowCropModal(false);
    setRawCropSrc(null);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      openPageCrop(e.target.files[0]);
    }
  };

  const removeSelectedImage = async () => {
    // Check if this is an existing banner on the server or just a newly selected file
    const isExistingBanner = currentPage?.bannerImageUrl && !selectedFile;
    
    if (isExistingBanner) {
      // Confirm before deleting from server
      if (window.confirm('Are you sure you want to delete this banner image? This action cannot be undone.')) {
        try {
          setIsUploading(true);
          await pageContentService.deleteBanner(currentPage.id);
          
          // Clear the banner from formData
          setFormData({ ...formData, bannerImageUrl: '' });
          setPreviewUrl('');
          setShowExistingBanner(false);
          
          setToast({
            id: Date.now().toString(),
            type: 'success',
            title: 'Success',
            message: 'Banner image deleted successfully'
          });
        } catch (error) {
          console.error('Failed to delete banner:', error);
          setToast({
            id: Date.now().toString(),
            type: 'error',
            title: 'Error',
            message: 'Failed to delete banner image. Please try again.'
          });
        } finally {
          setIsUploading(false);
        }
      }
    } else {
      // Just remove the newly selected file (not saved yet)
      setSelectedFile(null);
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl('');
      setShowExistingBanner(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const updateLink = (sectionIndex: number, linkIndex: number, field: 'text' | 'url', value: string) => {
    const newSections = [...formData.sections];
    newSections[sectionIndex].links[linkIndex][field] = value;
    setFormData({ ...formData, sections: newSections });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">Information Page Settings</h1>
      </div>

      {pages.length === 0 ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="text-center py-8 text-gray-500">
            No Information page found. Please contact your administrator.
          </div>
        </div>
      ) : currentPage ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-md p-6">
          <div className="space-y-6">


            {/* Banner Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Banner Image</label>
              
              {/* Current/Preview Image */}
              {(showExistingBanner && currentPage?.bannerImageUrl) || previewUrl ? (
                <div className="mb-4">
                  <div className="relative inline-block">
                    {showExistingBanner && currentPage?.bannerImageUrl && !previewUrl ? (
                      <AuthenticatedImage
                        src={`/api/page-content/banner/${currentPage.id}`}
                        alt="Banner preview"
                        className="w-full max-w-md h-32 object-cover rounded-lg shadow-sm border"
                      />
                    ) : (
                      <img 
                        src={previewUrl} 
                        alt="Banner preview" 
                        className="w-full max-w-md h-32 object-cover rounded-lg shadow-sm border"
                      />
                    )}
                    <button
                      type="button"
                      onClick={removeSelectedImage}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                      disabled={isUploading}
                    >
                      {isUploading ? (
                        <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <XMarkIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {selectedFile && (
                    <p className="text-sm text-gray-500 mt-2">Selected: {selectedFile.name}</p>
                  )}
                </div>
              ) : (
              /* Upload Area - Only shown when no image */
              <div
                className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${
                  dragActive 
                    ? 'border-primary-400 bg-primary-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileInputChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                
                <div className="text-center">
                  <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-900">
                      Upload Banner Image
                    </p>
                    <p className="text-sm text-gray-500">
                      Drag and drop your image here, or click to browse
                    </p>
                    <p className="text-xs text-gray-500 mt-2">PNG, JPG, WEBP up to 10MB</p>
                    <p className="text-xs text-gray-500">Image will be cropped to 16:9 (2000×1125px)</p>
                  </div>
                </div>
              </div>
              )}
              
              {/* Upload Progress */}
              {isUploading && (
                <div className="mt-2">
                  <div className="bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Uploading... {uploadProgress}%</p>
                </div>
              )}
            </div>



            {/* Fixed Sections */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">Page Sections</label>
              
              {formData.sections.map((section, sectionIndex) => (
                <div key={sectionIndex} className="border rounded-lg p-4 mb-4 bg-gray-50">
                  <div className="mb-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">{section.header}</h3>
                  </div>
                  
                  <div className="space-y-3">
                    {section.links.map((link, linkIndex) => (
                      <div key={linkIndex} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Link Text {linkIndex + 1} {linkIndex < (sectionIndex === 0 ? 3 : 2) && <span className="text-red-500">*</span>}
                          </label>
                          <input
                            type="text"
                            value={link.text}
                            onChange={(e) => updateLink(sectionIndex, linkIndex, 'text', e.target.value)}
                            className="w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
                            placeholder={`${section.header} link ${linkIndex + 1} text`}
                            required={linkIndex < (sectionIndex === 0 ? 3 : 2)}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Link URL {linkIndex + 1} {linkIndex < (sectionIndex === 0 ? 3 : 2) && <span className="text-red-500">*</span>}
                          </label>
                          <input
                            type="url"
                            value={link.url}
                            onChange={(e) => updateLink(sectionIndex, linkIndex, 'url', e.target.value)}
                            className="w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
                            placeholder={`https://example.com/${section.header.toLowerCase()}-link-${linkIndex + 1}`}
                            required={linkIndex < (sectionIndex === 0 ? 3 : 2)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-3 text-xs text-gray-500">
                    {sectionIndex === 0 
                      ? 'Contact Us section requires exactly 3 links' 
                      : 'Packages section requires exactly 2 links'
                    }
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end pt-4">
              <button
                onClick={handleSave}
                disabled={isUploading}
                className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isUploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Uploading...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showCropModal && rawCropSrc && (
        <ImageCropModal
          imageSrc={rawCropSrc}
          aspectRatio={16 / 9}
          title="Crop Banner Image"
          outputWidth={2000}
          outputHeight={1125}
          onCropComplete={handlePageCropComplete}
          onCancel={handlePageCropCancel}
        />
      )}

      {toast && (
        <div className="fixed top-4 right-4 z-[100] pointer-events-none">
          <div className="pointer-events-auto">
            <Toast
              id={toast.id}
              type={toast.type}
              title={toast.title}
              message={toast.message}
              onClose={() => setToast(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PageContent;